import React, { useMemo, useState } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { PROPOSAL_BUNDLE_STATE, type ProposalBundle } from '../../api/proposalBundles';
import { formatProposalPrice } from '../../utils/priceFormatter';
import { formatHotelLocationInfo } from '../../utils/routeInfoFormatter';
import HotelDetailModal from './HotelDetailModal';

// TODO: TransportCard でも同様の定義をしているため、共通utilへの切り出しを検討する
const CIRCLE_NUMBERS = ['①', '②', '③', '④', '⑤'] as const;

interface Props {
  bundle: ProposalBundle;
  productNumber: number;
  includeTax: boolean;
  onSelect?: (bundleId: number, itemId: number) => void;
  onClose?: (bundleId: number) => void;
  disabled?: boolean;
  tripId: number;
}

const HotelCard = ({ bundle, productNumber, includeTax, onSelect, onClose, disabled = false, tripId }: Props) => {
  const { theme } = useAITTheme();
  const isRejected = bundle.state === PROPOSAL_BUNDLE_STATE.REJECTED;
  const styles = useMemo(() => getStyles(theme, isRejected), [theme, isRejected]);
  const stayDays = bundle.stayDays ?? null;
  const locationInfo = useMemo(
    () => formatHotelLocationInfo(bundle.destination, bundle.departureDate, bundle.returnDate),
    [bundle.destination, bundle.departureDate, bundle.returnDate]
  );
  const priceHeaderNote = stayDays && stayDays > 0 ? `（${stayDays}泊合計）` : '（1泊1室）';
  const [detailModalItemId, setDetailModalItemId] = useState<number | null>(null);

  const handleSelect = (itemId: number) => {
    onSelect?.(bundle.id, itemId);
  };

  const handleClose = () => {
    onClose?.(bundle.id);
  };

  const handleOpenDetail = (itemId: number) => {
    setDetailModalItemId(itemId);
  };

  const handleCloseDetail = () => {
    setDetailModalItemId(null);
  };

  return (
    <div style={styles.card}>
      {!isRejected && (
        <button
          type="button"
          style={{
            ...styles.closeButton,
            ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {})
          }}
          onClick={handleClose}
          disabled={disabled}
        >
          ×
        </button>
      )}

      <div style={styles.header}>
        <span style={styles.productBadge}>商品{productNumber}</span>
        <span style={styles.categoryLabel}>🏨 宿泊施設{locationInfo}</span>
        {isRejected && <span style={styles.rejectedLabel}>予約しない</span>}
      </div>

      <table style={styles.table}>
        <thead>
          <tr style={styles.tableHeaderRow}>
            <th style={styles.tableHeaderCellName}>ホテル名</th>
            <th style={styles.tableHeaderCellDetail}>情報</th>
            <th style={styles.tableHeaderCellStay}>泊数</th>
            <th style={styles.tableHeaderCellPrice}>
              料金<span style={styles.priceUnitNote}>{priceHeaderNote}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {bundle.items.map((item, index) => {
            const circleNumber = CIRCLE_NUMBERS[index] || `${index + 1}`;
            const hotelName = item.title || '-';
            const rowStayDays = bundle.stayDays ?? item.stayDays ?? null;
            const nights = rowStayDays && rowStayDays > 0 ? rowStayDays : 1;
            const totalPrice = item.price !== null ? item.price * nights : null;
            const totalPriceWithTax = item.priceWithTax !== null ? item.priceWithTax * nights : null;
            const priceDisplay = formatProposalPrice({
              price: totalPrice,
              priceWithTax: totalPriceWithTax,
              includeTax
            });
            const unitPriceDisplay = formatProposalPrice({
              price: item.price,
              priceWithTax: item.priceWithTax,
              includeTax
            });
            const stayDisplay = rowStayDays && rowStayDays > 0 ? `${rowStayDays}泊${rowStayDays + 1}日` : '-';
            const rowStyle = {
              ...styles.row,
              backgroundColor: item.selected ? '#e6f4fb' : 'transparent'
            };
            const selectButtonStyle = {
              ...styles.selectButton,
              cursor: (item.selected || disabled ? 'not-allowed' : 'pointer') as 'pointer' | 'not-allowed',
              opacity: item.selected || disabled ? 0.5 : 1
            };
            const handleRowSelect = () => {
              if (!item.selected && !disabled) {
                handleSelect(item.id);
              }
            };

            return (
              <tr key={item.id} style={rowStyle}>
                <td style={styles.cellName}>
                  <div style={styles.nameContainer}>
                    <div style={styles.numberCircle}>{circleNumber}</div>
                    <div>
                      <span>{hotelName}</span>
                    </div>
                  </div>
                </td>
                <td style={styles.cellDetail}>
                  <button type="button" style={styles.detailButton} onClick={() => handleOpenDetail(item.id)}>
                    詳細
                  </button>
                </td>
                <td style={styles.cellStay}>{stayDisplay}</td>
                <td style={styles.cellPrice}>
                  <div style={styles.priceContainer}>
                    <div style={styles.priceTextBlock}>
                      <span style={styles.totalPriceText}>{priceDisplay}</span>
                      <span style={styles.unitPriceText}>{unitPriceDisplay} /泊</span>
                    </div>
                    <button
                      type="button"
                      style={selectButtonStyle}
                      onClick={handleRowSelect}
                      disabled={item.selected || disabled}
                    >
                      選択
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {detailModalItemId !== null && (
        <HotelDetailModal
          open={detailModalItemId !== null}
          onClose={handleCloseDetail}
          tripId={tripId}
          itemId={detailModalItemId}
        />
      )}
    </div>
  );
};

const getStyles = (theme: AITTheme, isRejected: boolean) => ({
  card: {
    backgroundColor: theme.whiteColor,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    border: `2px solid ${isRejected ? theme.grayBorderColor : theme.linkColor}`,
    position: 'relative' as const,
    opacity: isRejected ? 0.6 : 1
  },
  closeButton: {
    position: 'absolute' as const,
    top: '12px',
    right: '12px',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: 'none',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    fontSize: '18px',
    fontWeight: 300 as const,
    lineHeight: 1,
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  header: {
    fontWeight: 700 as const,
    color: isRejected ? theme.grayTextColor : theme.linkColor,
    marginBottom: '16px',
    fontSize: '17px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  productBadge: {
    backgroundColor: isRejected ? theme.grayTextColor : theme.linkColor,
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600 as const
  },
  categoryLabel: {
    fontSize: '14px',
    textDecoration: isRejected ? 'line-through' : 'none'
  },
  rejectedLabel: {
    backgroundColor: '#f5f5f5',
    color: theme.grayTextColor,
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600 as const,
    marginLeft: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const
  },
  tableHeaderRow: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ececec'
  },
  tableHeaderCellName: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px',
    width: '42%'
  },
  tableHeaderCellDetail: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px',
    width: '15%'
  },
  tableHeaderCellStay: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px',
    width: '15%'
  },
  tableHeaderCellPrice: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px',
    width: '28%'
  },
  priceUnitNote: {
    marginLeft: '4px',
    fontSize: '11px',
    color: isRejected ? theme.grayTextColor : theme.linkColor
  },
  row: {
    borderBottom: '1px solid #ececec'
  },
  cellName: {
    padding: '12px 8px',
    fontSize: '13px',
    fontWeight: 500 as const,
    verticalAlign: 'middle' as const
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  cellDetail: {
    padding: '12px 8px',
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const
  },
  cellStay: {
    padding: '12px 8px',
    fontSize: '13px',
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const
  },
  cellPrice: {
    padding: '12px 8px',
    textAlign: 'right' as const,
    verticalAlign: 'middle' as const
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '10px',
    fontWeight: 700 as const,
    color: theme.linkColor,
    fontSize: '15px'
  },
  priceTextBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end' as const,
    gap: '2px'
  },
  totalPriceText: {
    fontWeight: 700 as const,
    color: theme.linkColor,
    fontSize: '15px'
  },
  unitPriceText: {
    fontWeight: 400 as const,
    color: '#888888',
    fontSize: '11px'
  },
  numberCircle: {
    width: '28px',
    height: '28px',
    minWidth: '28px',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700 as const,
    fontSize: '12px'
  },
  detailButton: {
    backgroundColor: '#ffffff',
    color: theme.linkColor,
    border: `1px solid ${theme.linkColor}`,
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500 as const,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const
  },
  selectButton: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600 as const,
    whiteSpace: 'nowrap' as const
  }
});

export default HotelCard;
